/**
 * Main Application
 * Integrates Phylocanvas tree viewer with metadata table
 */

import { PhylocanvasRenderer } from '../renderers/phylocanvas-renderer';
import { TheiaPhyloViewerRenderer } from '../renderers/theiaphyloviewer-renderer';
import {
  chooseDefaultRenderer,
  hasReliableWebGL,
  rendererProvidesOwnAppearanceUI,
  sanitizeRendererType,
  type RendererType,
} from '../renderers/renderer-default';
import {
  APPEARANCE_FONT_FAMILIES,
  APPEARANCE_LAYOUTS,
  APPEARANCE_NODE_SHAPES,
  applyAppearanceToRenderer,
  BLOCK_SIZE_RANGE,
  BRANCH_ZOOM_RANGE,
  formatZoomMultiplier,
  HALO_RADIUS_RANGE,
  HALO_WIDTH_RANGE,
  LABEL_SIZE_RANGE,
  NODE_SIZE_RANGE,
  PADDING_RANGE,
  SCALEBAR_DIGITS_RANGE,
  SCALEBAR_FONT_SIZE_RANGE,
  SCALEBAR_OPACITY_RANGE,
  SCALEBAR_WIDTH_RANGE,
  STEP_ZOOM_RANGE,
  ZOOM_RANGE,
} from './appearance-settings';
import {
  appearanceToCanonical,
  canonicalHasAppearance,
  canonicalToAppearance,
  foldLegacyAppearance,
  foldLegacyColorBy,
} from './settings-projection';
import { buildLeafColorMap, legendEntries } from './color-by-leaves';
import {
  fieldPalettesMap,
  planMetadataDispatch,
  resolveColorByPaletteName,
} from './metadata-dispatch';
import { MetadataLegend } from './metadata-legend';
import { UploadHandler } from '../upload/upload-handler';
import { DataLoader } from '../data/data-loader';
import { colorManager } from '../color/color-manager';
import { TreeMetadataSync } from '../sync/tree-metadata-sync';
import { TableauSettingsManager } from '../tableau/settings-manager';
import { ColorBrewerPalettes } from '../color/color-palettes';

declare const PhylocanvasGL: any;
declare const tableau: any;

/**
 * Persisted legend colour overrides: metadata-block colours keyed
 * `field:value` and tip colours keyed `leaf:field:value`.
 */
export interface CustomColorSettings {
  customColors: Record<string, string> | null;
  leafCustomColors: Record<string, string> | null;
}

export class TheiaPhyloViewer {
  tree: string | null;
  metadata: any[];
  columnTypes: Record<string, string>;
  treeRenderer: any;
  rendererType: string;
  selectedRows: Set<number>;
  sortColumn: string | null;
  sortDirection: string;
  isTableCollapsed: boolean;
  tooltipEnabled: boolean;
  tooltipFields: string[];
  settingsKey: string;

  rendererSettings: {
    phylocanvas: { alignmentLength: number | null; showScaleBar: boolean };
  };

  searchQuery: string;
  allTipLabels: string[];
  matchedTips: string[];
  currentMatchIndex: number;

  filteredTipIds: string[] | null;

  displayAsField: string | null;
  originalTipLabels: Map<string, string>;

  _controlsInitialized?: boolean;
  _resizeHandlerAdded?: boolean;
  _searchControlsInitialized?: boolean;
  _resizeHandleInitialized?: boolean;

  constructor() {
    this.tree = null;
    this.metadata = [];
    this.columnTypes = {};
    this.treeRenderer = null;
    this.rendererType = 'phylocanvas'; // 'phylocanvas' | 'theiaphyloviewer'
    this.selectedRows = new Set();
    this.sortColumn = null;
    this.sortDirection = 'asc'; // 'asc' or 'desc'
    this.isTableCollapsed = false;
    this.tooltipEnabled = true;
    this.tooltipFields = []; // All fields by default
    this.settingsKey = 'theia-phylo-viewer-settings';

    this.rendererSettings = {
      phylocanvas: {
        alignmentLength: null,
        showScaleBar: true
      }
    };

    // Search state
    this.searchQuery = '';
    this.allTipLabels = [];
    this.matchedTips = [];
    this.currentMatchIndex = -1;

    // Subtree filter state
    this.filteredTipIds = null; // null = show all, array = show only these tips

    // Display state
    this.displayAsField = null; // Field to use for tip labels display
    this.originalTipLabels = new Map(); // Store original labels for restoration
  }

  /**
   * Save settings to localStorage
   */
  saveSettings(settings: Record<string, any>): void {
    try {
      const currentSettings = this.loadSettings() || {};
      const updatedSettings = { ...currentSettings, ...settings };
      // Deep-merge the canonical phylocanvas bag so partial writes (the SVG
      // appearance panel) never clobber keys they did not set, and so a
      // phylocanvas full-bag save preserves the SVG-only `svgExtras` it never
      // writes. All other keys keep the shallow top-level merge.
      if (settings.phylocanvas && typeof settings.phylocanvas === 'object') {
        updatedSettings.phylocanvas = {
          ...(currentSettings.phylocanvas as Record<string, unknown> | undefined),
          ...settings.phylocanvas,
        };
      }
      localStorage.setItem(this.settingsKey, JSON.stringify(updatedSettings));
      console.log('[TheiaPhyloViewer] Settings saved:', settings);
      console.log('[TheiaPhyloViewer] Full settings:', updatedSettings);
    } catch (error) {
      console.error('[TheiaPhyloViewer] Failed to save settings:', error);
    }
  }

  /**
   * Load settings from localStorage
   */
  loadSettings(): Record<string, any> | null {
    try {
      const saved = localStorage.getItem(this.settingsKey);
      if (saved) {
        const settings = JSON.parse(saved);
        console.log('[TheiaPhyloViewer] Settings loaded:', settings);
        return settings;
      } else {
        console.log('[TheiaPhyloViewer]  No saved settings found');
      }
    } catch (error) {
      console.error('[TheiaPhyloViewer] Failed to load settings:', error);
    }
    return null;
  }

  /**
   * Read the canonical (phylocanvas-vocabulary) settings bag the SVG appearance
   * panel projects to/from. The standalone reads it from localStorage; the Tableau
   * subclass overrides this to read the Tableau Settings API instead, so the panel
   * round-trips through the same bag the phylocanvas renderer uses on each surface.
   */
  protected loadCanonicalAppearanceBag(): Record<string, unknown> {
    const phylo = this.loadSettings()?.phylocanvas;
    return phylo && typeof phylo === 'object' ? (phylo as Record<string, unknown>) : {};
  }

  /**
   * Read the persisted legend colour overrides. The standalone reads them from
   * localStorage; the Tableau subclass overrides this to read the Tableau
   * Settings API.
   */
  protected loadCustomColorSettings(): CustomColorSettings {
    const settings = this.loadSettings();
    return {
      customColors: settings?.customColors ?? null,
      leafCustomColors: settings?.leafCustomColors ?? null,
    };
  }

  /**
   * Load the persisted legend colour overrides into TreeMetadataSync so every
   * colour map generated afterwards (tips and metadata blocks) honours them.
   */
  restoreCustomColors(): void {
    const { customColors, leafCustomColors } = this.loadCustomColorSettings();
    TreeMetadataSync.loadCustomColors(customColors);
    TreeMetadataSync.loadLeafCustomColors(leafCustomColors);
  }

  /**
   * One-time fold of legacy standalone settings into the canonical `phylocanvas`
   * bag, then drop the legacy keys: the old `appearance` bag and the top-level
   * colour-by keys (`colorByField`/`colorByEnabled`/`colorByPalette`) an earlier
   * build wrote outside the bag. Idempotent: a no-op once migrated (or when no
   * legacy data exists, e.g. a pure Tableau host).
   */
  private migrateLegacyAppearance(): void {
    const current = this.loadSettings() ?? {};
    const afterAppearance = foldLegacyAppearance(current);
    const afterColorBy = foldLegacyColorBy(afterAppearance ?? current);
    const folded = afterColorBy ?? afterAppearance;
    if (!folded) return;
    try {
      localStorage.setItem(this.settingsKey, JSON.stringify(folded));
      console.log('[TheiaPhyloViewer]  Migrated legacy settings into phylocanvas bag');
    } catch (error) {
      console.error('[TheiaPhyloViewer] Failed to migrate legacy settings:', error);
    }
  }

  /**
   * Clear saved settings
   */
  clearSettings(): void {
    try {
      localStorage.removeItem(this.settingsKey);
      console.log('[TheiaPhyloViewer] Settings cleared');
    } catch (error) {
      console.error('[TheiaPhyloViewer] Failed to clear settings:', error);
    }
  }

  /**
   * Resolve the renderer to use when no explicit choice has been saved.
   * Capability-aware via the WebGL probe; the standalone app has no Tableau host
   * context, so it passes undefined. Overridden in the Tableau subclass.
   */
  resolveDefaultRendererType(): RendererType {
    return chooseDefaultRenderer(undefined, hasReliableWebGL());
  }

  /**
   * Persist the active renderer type. Base merges it into localStorage; the
   * Tableau subclass overrides this to use the Tableau Settings API.
   */
  async persistRendererType(rendererType: string): Promise<void> {
    this.saveSettings({ rendererType });
  }

  /**
   * Save all current settings manually
   */
  async saveAllCurrentSettings(): Promise<void> {
    try {
      // Save renderer type
      await this.persistRendererType(this.rendererType);

      // Save panel sizes
      this.savePanelSizes();

      // Save phylocanvas settings if using phylocanvas renderer
      if (this.treeRenderer && this.treeRenderer.saveSettings) {
        await this.treeRenderer.saveSettings();
      }

      // Show success feedback
      this.showNotification('Settings saved successfully!', 'success');
      console.log('[TheiaPhyloViewer] All settings saved manually');
    } catch (error) {
      console.error('[TheiaPhyloViewer] Failed to save settings:', error);
      this.showNotification('Failed to save settings', 'error');
    }
  }

  /**
   * Snapshot the live (in-memory) state of the currently mounted renderer into the
   * canonical settings bag. The phylocanvas renderer holds toolbar state, tree
   * layout, scalebar, zoom, node/label sizing, label toggles, only in memory and
   * flushes it to the bag on demand; a renderer switch is one such point, so without
   * this snapshot the incoming renderer would restore a stale bag (e.g. the previous
   * layout). Its colour-by is mirrored onto the instance as the user edits it, so the
   * full snapshot stays coherent. The SVG renderer persists every change eagerly and
   * exposes no snapshot, so it is simply skipped (its bag is already current).
   */
  async snapshotCurrentRendererSettings(): Promise<void> {
    if (this.treeRenderer && this.treeRenderer.saveSettings) {
      await this.treeRenderer.saveSettings();
    }
  }

  /**
   * Show notification message
   */
  showNotification(message: string, type: string = 'info'): void {
    // Create notification element if it doesn't exist
    let notification = document.getElementById('settings-notification') as HTMLElement | null;
    if (!notification) {
      notification = document.createElement('div');
      notification.id = 'settings-notification';
      notification.style.cssText = `
        position: fixed;
        top: 70px;
        right: 20px;
        padding: 12px 20px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        z-index: 10000;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      `;
      document.body.appendChild(notification);
    }

    // Set color based on type
    const colors: Record<string, { bg: string; text: string }> = {
      success: { bg: '#4caf50', text: 'white' },
      error: { bg: '#f44336', text: 'white' },
      info: { bg: '#2196f3', text: 'white' }
    };
    const color = colors[type] ?? colors['info']!;
    notification.style.backgroundColor = color.bg;
    notification.style.color = color.text;
    notification.textContent = message;

    // Show notification
    notification.style.opacity = '1';

    // Hide after 2 seconds
    setTimeout(() => {
      (notification as HTMLElement).style.opacity = '0';
    }, 2000);
  }

  /**
   * Initialize the application
   */
  async init(config: Record<string, any>): Promise<void> {
    try {
      // Show loading
      document.getElementById('loading')!.classList.remove('hidden');

      // Load saved settings
      const savedSettings = this.loadSettings();

      // Load data
      const data = await DataLoader.loadData(config);
      this.metadata = data.metadata as any[];
      this.tree = data.tree as string | null;

      // Detect column types
      this.columnTypes = DataLoader.detectColumnTypes(this.metadata);

      // Parse tree data
      const treeData = this.parseTree(this.tree);

      // Find label field (auto-detect, use config, or first column)
      let labelField: string;
      if (config.labelField) {
        labelField = config.labelField;
      } else {
        // Auto-detect best matching field
        labelField = this.autoDetectLabelField(treeData, this.metadata);
      }

      // Build tree-metadata mapping
      TreeMetadataSync.buildLabelMapping(treeData, this.metadata, labelField);

      // Apply saved renderer type, or pick a capability-aware default when unset
      // or invalid (e.g. a removed renderer still recorded in saved settings).
      this.rendererType = sanitizeRendererType(
        savedSettings?.rendererType,
        this.resolveDefaultRendererType(),
      );

      // Initialize UI
      this.initControls();
      this.renderTable();
      this.renderTree();

      // Apply saved panel sizes (after render so panels exist)
      if (savedSettings && savedSettings.panelSizes) {
        this.applyPanelSizes(savedSettings.panelSizes);
      }

      // Hide loading
      document.getElementById('loading')!.classList.add('hidden');

      console.log('[TheiaPhyloViewer] Initialized successfully');
    } catch (error: any) {
      console.error('[TheiaPhyloViewer] Initialization failed:', error);
      alert('Failed to initialize viewer: ' + error.message);
    }
  }

  /**
   * Parse tree string to extract leaf labels
   */
  parseTree(treeString: string | null): { leaves: Array<{ id: string }> } {
    // Parse the tree to extract leaf labels
    const leaves = this.extractLeavesFromNewick(treeString ?? '');

    return { leaves };
  }

  /**
   * Extract leaf labels from Newick string
   */
  extractLeavesFromNewick(newick: string): Array<{ id: string }> {
    const leaves: Array<{ id: string }> = [];
    // Match leaf labels (text before colons)
    // Newick format: label:branch_length
    // Supports alphanumeric, underscores, hyphens, pipes, dots, slashes, and other special chars
    // The pattern matches any non-special-Newick characters before a colon
    // Special Newick chars are: () , ; : (tree structure)
    const regex = /([^(),;:\s]+):/g;
    let match;

    while ((match = regex.exec(newick)) !== null) {
      const label = match[1]!.trim();
      if (label) {
        leaves.push({ id: label });
      }
    }

    return leaves;
  }

  /**
   * Initialize controls (one-time setup)
   */
  initControls(): void {
    // Update search controls even if already initialized (tree may have changed)
    this.initSearchControls();

    // Skip if already initialized
    if (this._controlsInitialized) {
      this.updateControlDropdowns();
      return;
    }

    const rendererSelect = document.getElementById('rendererType') as HTMLSelectElement;
    const clearSelectionBtn = document.getElementById('clear-selection-btn') as HTMLButtonElement;
    const tablePanelHeader = document.getElementById('table-panel-header') as HTMLElement;

    // Initialize resize handle
    this.initResizeHandle();

    // Reflect the resolved effective renderer (matches what we render)
    rendererSelect.value = this.rendererType;

    // Renderer change handler
    rendererSelect.addEventListener('change', async (e) => {
      const nextRenderer = (e.target as HTMLSelectElement).value;

      // Snapshot the OUTGOING renderer's live state into the canonical bag before we
      // tear it down, so the incoming renderer restores the user's full configuration
      //, layout, scalebar, zoom, sizing, not just the colour-by the dedicated
      // controls persist eagerly. Awaited so the write lands before renderTree() reads
      // the bag back to seed the new renderer.
      await this.snapshotCurrentRendererSettings();

      this.rendererType = nextRenderer;
      this.renderTree();

      // Reapply current state
      if (TreeMetadataSync.state.colorByField) {
        this.updateTreeColors();
      }
      if (this.displayAsField) {
        this.updateTreeTipLabels(this.displayAsField);
      }
      if (this.selectedRows.size > 0) {
        const leafIds = TreeMetadataSync.rowSelectionsToTreeLeaves(
          Array.from(this.selectedRows)
        );
        this.highlightTreeNodes(leafIds);
      }

      void this.persistRendererType(nextRenderer);
    });

    // Clear selection handler - metadata table button
    clearSelectionBtn.addEventListener('click', () => {
      this.clearSelection();
    });

    // Note: Clear selection handler for tree panel button is set up in addClearSelectionButton()

    // Table collapse handler
    tablePanelHeader.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.btn-small')) return; // Don't collapse when clicking buttons
      this.toggleTableCollapse();
    });

    this._controlsInitialized = true;

    // Populate dropdowns
    this.updateControlDropdowns();
  }

  /**
   * Setup color by controls (called after phylocanvas menu is created)
   */
  setupColorByControls(): void {
    const enableColorByCheckbox = document.getElementById('enableColorBy') as HTMLInputElement | null;
    const colorBySelect = document.getElementById('colorByField') as HTMLSelectElement | null;
    const colorPaletteBtn = document.getElementById('colorPaletteBtn') as HTMLButtonElement | null;

    if (!enableColorByCheckbox || !colorBySelect || !colorPaletteBtn) {
      console.warn('[TheiaPhyloViewer] Color by controls not found');
      return;
    }

    // Enable Color by toggle handler
    enableColorByCheckbox.addEventListener('change', (e) => {
      const enabled = (e.target as HTMLInputElement).checked;
      TreeMetadataSync.setColorByEnabled(enabled);

      // Update renderer's internal state to keep in sync
      if (this.treeRenderer) {
        this.treeRenderer.enableColorBy = enabled;
      }

      // Enable/disable the dropdown and palette button
      colorBySelect!.disabled = !enabled;
      colorPaletteBtn!.disabled = !enabled || !colorBySelect!.value;

      if (enabled && colorBySelect!.value) {
        // Apply coloring with current field selection
        const field = colorBySelect!.value;
        const paletteName = this.treeRenderer?.fieldPalettes?.get(field) || 'Set1';

        TreeMetadataSync.setLeafPalette('qualitative', paletteName);
        TreeMetadataSync.generateLeafColorMap(this.metadata, field, this.columnTypes);
        this.updateTreeColors();

        // Update palette preview
        this.updateLeafColorPalettePreview(paletteName);

        // Update legend to show leaf colors
        if (this.treeRenderer) {
          this.treeRenderer.updateLegend(
            this.treeRenderer.metadataBlocks || [],
            this.treeRenderer.metadata || {}
          );
        }
      } else if (!enabled) {
        // Reset colors but preserve field selection for when user toggles back on
        this.resetTreeColors();

        // Hide palette preview when disabled
        this.updateLeafColorPalettePreview(null);

        // Update legend to remove leaf colors
        if (this.treeRenderer) {
          this.treeRenderer.updateLegend(
            this.treeRenderer.metadataBlocks || [],
            this.treeRenderer.metadata || {}
          );
        }
      }

      // Persist into the canonical phylocanvas bag both renderers read, writing both
      // enablement spellings so the SVG panel and phylocanvas renderer stay in sync.
      this.saveSettings({ phylocanvas: { colorByEnabled: enabled, enableColorBy: enabled } });
    });

    // Color by change handler
    colorBySelect.addEventListener('change', (e) => {
      const field = (e.target as HTMLSelectElement).value;

      // Enable/disable palette button based on field selection
      colorPaletteBtn!.disabled = !field || !enableColorByCheckbox!.checked;

      if (field && enableColorByCheckbox!.checked) {
        // Get palette from ColorManager or use default
        let paletteName = 'Set1';
        if (colorManager.hasPalette(field)) {
          paletteName = colorManager.getPalette(field) as unknown as string;
        } else if (this.treeRenderer.fieldPalettes?.has(field)) {
          paletteName = this.treeRenderer.fieldPalettes.get(field);
        }

        console.log(`[TheiaPhyloViewer] Color by "${field}" using palette "${paletteName}"`);

        // Use LEAF-SPECIFIC color map generation (independent of metadata blocks)
        TreeMetadataSync.setLeafPalette('qualitative', paletteName);
        TreeMetadataSync.generateLeafColorMap(this.metadata, field, this.columnTypes);
        this.updateTreeColors();

        // Update palette preview
        this.updateLeafColorPalettePreview(paletteName);

        // Update renderer's internal state to keep in sync
        if (this.treeRenderer) {
          this.treeRenderer.colorByField = field;
          this.treeRenderer.colorByPalette = { category: 'qualitative', name: paletteName };
          // Update legend to show leaf colors
          this.treeRenderer.updateLegend(
            this.treeRenderer.metadataBlocks || [],
            this.treeRenderer.metadata || {}
          );
        }

        // Selecting a field while the checkbox is on means colour-by is active;
        // persist field and enablement together into the canonical phylocanvas bag.
        this.saveSettings({
          phylocanvas: { colorByField: field, colorByEnabled: true, enableColorBy: true },
        });
      } else {
        // Reset colors to default when "None" is selected
        this.resetTreeColors();

        // Hide palette preview
        this.updateLeafColorPalettePreview(null);

        // Update renderer's internal state
        if (this.treeRenderer) {
          this.treeRenderer.colorByField = null;
          // Update legend to remove leaf colors
          this.treeRenderer.updateLegend(
            this.treeRenderer.metadataBlocks || [],
            this.treeRenderer.metadata || {}
          );
        }

        // Clear the field in the canonical phylocanvas bag (None selected).
        this.saveSettings({ phylocanvas: { colorByField: null } });
      }
    });

    // Palette button handler
    colorPaletteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const field = colorBySelect!.value;
      if (field) {
        this.showPaletteMenu(e.target as HTMLElement, field);
      }
    });

    console.log('[TheiaPhyloViewer] Color by controls setup complete');
  }

  /**
   * Update control dropdowns with current metadata
   */
  updateControlDropdowns(): void {
    // No longer needed - label field is set via column headers
  }

  /**
   * Restore saved color by settings (called after controls are set up)
   */
  restoreColorBySettings(): void {
    // Colour-by lives in the canonical phylocanvas bag both renderers share, so a
    // switch from the SVG renderer (or a reload) restores the same enablement,
    // field, and palette the SVG panel and phylocanvas renderer also read.
    const bag = this.loadCanonicalAppearanceBag();

    const enableColorByCheckbox = document.getElementById('enableColorBy') as HTMLInputElement | null;
    const colorBySelect = document.getElementById('colorByField') as HTMLSelectElement | null;
    const colorPaletteBtn = document.getElementById('colorPaletteBtn') as HTMLButtonElement | null;

    if (!enableColorByCheckbox || !colorBySelect || !colorPaletteBtn) {
      return;
    }

    const enabledRaw = bag.colorByEnabled ?? bag.enableColorBy;
    const enabled = enabledRaw === true;
    const field = typeof bag.colorByField === 'string' ? bag.colorByField : null;
    const palette = bag.colorByPalette as { category?: string; name?: string } | undefined;

    // Restore enabled state. Mirror onto the renderer instance too: a switch-time
    // snapshot reads the renderer's own colour-by fields, so leaving them `undefined`
    // here would make that snapshot JSON-strip colour-by from the shared bag (and the
    // phylocanvas "Save settings" button would lose it for the same reason).
    if (enabledRaw !== undefined) {
      enableColorByCheckbox.checked = enabled;
      TreeMetadataSync.setColorByEnabled(enabled);
      colorBySelect.disabled = !enabled;
      if (this.treeRenderer) this.treeRenderer.enableColorBy = enabled;
    }

    // Restore field selection
    if (field) {
      colorBySelect.value = field;
      colorPaletteBtn.disabled = !enabled;
      if (this.treeRenderer) this.treeRenderer.colorByField = field;

      // Restore palette if available
      if (palette && palette.category && palette.name) {
        // Use LEAF-SPECIFIC palette setting
        TreeMetadataSync.setLeafPalette(palette.category, palette.name);
        if (this.treeRenderer) {
          this.treeRenderer.colorByPalette = { category: palette.category, name: palette.name };
        }
      }

      // Apply colors if enabled - use LEAF-SPECIFIC color map
      if (enabled && this.metadata) {
        TreeMetadataSync.generateLeafColorMap(this.metadata, field, this.columnTypes);
        this.updateTreeColors();

        // Show palette preview
        const paletteName = palette?.name || 'Set1';
        this.updateLeafColorPalettePreview(paletteName);
      }
    }

    console.log('[TheiaPhyloViewer] Color by settings restored');
  }

  /**
   * Replay a persisted metadata-block selection into the phylocanvas renderer.
   * Both renderers keep the selection in the canonical phylocanvas bag, so a switch
   * from the SVG renderer (which routes blocks through the shared appearance panel)
   * into phylocanvas must re-tick the gear-menu selection phylocanvas owns itself.
   * Runs on both surfaces: the bag read goes through loadCanonicalAppearanceBag(),
   * which the Tableau subclass redirects to the Tableau Settings API, exactly as the
   * inherited restoreColorBySettings() already does. On the Tableau load path
   * applyLoadedSettings() restores the same blocks, but restoreSavedMetadataBlocks()
   * short-circuits when the menu already matches, so the double call stays a single
   * apply -- the standalone switch path, with no load-time restore, applies once.
   */
  protected restorePhylocanvasMetadataBlocks(): void {
    if (this.rendererType !== 'phylocanvas') return;
    const bag = this.loadCanonicalAppearanceBag();

    // Mirror the per-field block palettes onto the renderer instance, the same way
    // restoreColorBySettings mirrors colorByPalette. Without this the freshly built
    // instance keeps its empty constructor Map, restoreSavedMetadataBlocks repaints
    // every column in the Set1 fallback, and -- worse -- the switch-out snapshot
    // flushes that empty map over the bag, so a round-trip through phylocanvas loses
    // the saved palettes. The bag is the source of truth, so this is a populate on a
    // switch and an idempotent re-set on the Tableau load path.
    if (this.treeRenderer) this.treeRenderer.fieldPalettes = fieldPalettesMap(bag);

    const fields = bag.selectedMetadataFields;
    if (Array.isArray(fields) && fields.length > 0) {
      this.treeRenderer?.restoreSavedMetadataBlocks?.(
        fields.filter((field): field is string => typeof field === 'string')
      );
    }
  }

  /**
   * Replay the persisted tooltip-field selection and phylocanvas axis zoom into a
   * freshly built renderer. Both live in the canonical phylocanvas bag, but unlike
   * colour-by and metadata blocks they had no a-la-carte restore here, so a renderer
   * switch dropped a curated tooltip back to "all fields" and reset the horizontal/
   * vertical stretch. Runs on both surfaces: the bag read goes through
   * loadCanonicalAppearanceBag(), which the Tableau subclass redirects to the Tableau
   * Settings API, exactly as the inherited restoreColorBySettings() already does. The
   * Tableau load paths re-apply the same values through applyLoadedSettings(), a
   * harmless idempotent double-apply since both read one Settings source.
   */
  protected restoreTooltipAndZoomSettings(): void {
    const renderer = this.treeRenderer;
    if (!renderer) return;
    const bag = this.loadCanonicalAppearanceBag();

    // Which metadata values a leaf's hover tooltip shows, resolved through the same
    // planner the Tableau load path uses: a stale field from a prior dataset is
    // dropped and an empty result keeps phylocanvas's show-all semantics. Both
    // renderers persist the choice in the shared bag but expose different setters.
    const fields = planMetadataDispatch({ tooltipFields: bag.tooltipFields }, TreeMetadataSync, {
      metadata: this.metadata,
      columnTypes: this.columnTypes,
    }).tooltipFields;
    if (fields.length > 0) {
      if (typeof renderer.setTooltipFields === 'function') {
        renderer.setTooltipFields(fields);
      } else if (typeof renderer.setTooltipConfig === 'function') {
        renderer.setTooltipConfig({ fields });
        document.querySelectorAll('.tooltip-field-checkbox').forEach((checkbox) => {
          const input = checkbox as HTMLInputElement;
          input.checked = fields.includes(input.value);
        });
      }
    }

    // Horizontal / vertical axis stretch -- phylocanvas only. Deferred so the tree
    // layout settles first, the same wait phylocanvas's own applyLoadedSettings()
    // takes before re-applying zoom.
    if (this.rendererType === 'phylocanvas') {
      const h =
        typeof bag.horizontalZoom === 'number' && Number.isFinite(bag.horizontalZoom)
          ? bag.horizontalZoom
          : undefined;
      const v =
        typeof bag.verticalZoom === 'number' && Number.isFinite(bag.verticalZoom)
          ? bag.verticalZoom
          : undefined;
      if (h !== undefined || v !== undefined) {
        setTimeout(() => {
          if (h !== undefined) renderer.setHorizontalZoom?.(h);
          if (v !== undefined) renderer.setVerticalZoom?.(v);
        }, 300);
      }
    }
  }

  /**
   * Setup leaf text control (called after phylocanvas menu is created)
   */
  setupTipLabelControl(): void {
    const displayAsSelect = document.getElementById('displayAsField') as HTMLSelectElement | null;

    if (!displayAsSelect) {
      console.warn('[TheiaPhyloViewer] Leaf text control not found');
      return;
    }

    // Leaf text field change handler
    displayAsSelect.addEventListener('change', (e) => {
      const field = (e.target as HTMLSelectElement).value;
      console.log('[TheiaPhyloViewer] Leaf text field changed to:', field);
      this.updateTreeTipLabels(field);
    });

    console.log('[TheiaPhyloViewer] Leaf text control setup complete');
  }

  /**
   * Render metadata table
   */
  renderTable(): void {
    console.log('[TheiaPhyloViewer] renderTable called, metadata:', {
      length: this.metadata?.length,
      hasFirst: !!this.metadata?.[0],
      firstRowKeys: this.metadata?.[0] ? Object.keys(this.metadata[0]) : []
    });

    const thead = document.getElementById('table-head') as HTMLElement;
    const tbody = document.getElementById('table-body') as HTMLElement;

    // Clear existing
    thead.innerHTML = '';
    tbody.innerHTML = '';

    if (!this.metadata || this.metadata.length === 0 || !this.metadata[0]) {
      console.log('[TheiaPhyloViewer] renderTable early return - no metadata');
      return;
    }

    // Get sorted data
    let sortedData = this.getSortedMetadata();

    // Filter data if subtree view is active
    if (this.filteredTipIds && this.filteredTipIds.length > 0) {
      const labelField = TreeMetadataSync.state.labelField || 'id';
      const filteredSet = new Set(this.filteredTipIds);
      sortedData = sortedData.filter(({ row }: { row: any }) => {
        const labelValue = row[labelField];
        return labelValue && filteredSet.has(labelValue);
      });
      console.log('[TheiaPhyloViewer] Filtered table to', sortedData.length, 'rows for subtree');
    }

    // Create header
    const headerRow = document.createElement('tr');
    const headers = Object.keys(this.metadata[0]!);

    // Add checkbox column header
    const checkboxTh = document.createElement('th');
    checkboxTh.className = 'checkbox-col';
    const selectAllCheckbox = document.createElement('input');
    selectAllCheckbox.type = 'checkbox';
    selectAllCheckbox.id = 'select-all-checkbox';
    selectAllCheckbox.addEventListener('change', (e) => {
      this.handleSelectAll((e.target as HTMLInputElement).checked);
    });
    checkboxTh.appendChild(selectAllCheckbox);
    headerRow.appendChild(checkboxTh);

    // Add data column headers with sort indicators and label field indicator
    const currentLabelField = TreeMetadataSync.state.labelField;
    headers.forEach((header) => {
      const th = document.createElement('th');
      th.className = 'sortable';

      // Check if this column is the current label field
      const isLabelField = currentLabelField === header;

      th.innerHTML = `
        <span class="header-content">
          <span class="header-name">${header}</span>
          ${isLabelField ? '<span class="label-field-indicator" title="Label Field">🏷️</span>' : ''}
        </span>
        <span class="sort-indicator">${this.getSortIndicator(header)}</span>
      `;

      // Add click handler for sorting (click on the main header area)
      th.addEventListener('click', (e) => {
        // Check if user clicked on the label field indicator area
        if ((e.target as HTMLElement).closest('.label-field-indicator') || e.shiftKey) {
          // Set as label field
          this.setLabelFieldFromColumn(header);
        } else {
          // Sort
          this.handleSort(header);
        }
      });

      // Add right-click to set as label field
      th.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this.setLabelFieldFromColumn(header);
      });

      // Add title tooltip
      if (isLabelField) {
        th.title = `🏷️ Label Field: ${header}\nThis field is used to match tree tips with metadata rows.\nRight-click or Shift+click to change.`;
      } else {
        th.title = `${header}\nRight-click or Shift+click to set as Label Field.\nLabel Field matches tree tips with metadata rows.`;
      }

      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);

    // Get mapped row indices
    const mappedRows = new Set(TreeMetadataSync.getMappedRows());

    // Create rows
    sortedData.forEach(({ row, originalIndex }: { row: any; originalIndex: number }) => {
      const tr = document.createElement('tr');
      tr.dataset['rowIndex'] = String(originalIndex);

      // Add class to indicate if row is mapped to tree
      if (mappedRows.has(originalIndex)) {
        tr.classList.add('mapped-to-tree');
      } else {
        tr.classList.add('unmapped-row');
      }

      // Add checkbox cell
      const checkboxTd = document.createElement('td');
      checkboxTd.className = 'checkbox-col';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = this.selectedRows.has(originalIndex);
      checkbox.addEventListener('change', (e) => {
        e.stopPropagation();
        this.handleCheckboxChange(originalIndex, (e.target as HTMLInputElement).checked);
      });
      checkboxTd.appendChild(checkbox);
      tr.appendChild(checkboxTd);

      // Add data cells
      headers.forEach(header => {
        const td = document.createElement('td');
        td.textContent = row[header] || '';
        tr.appendChild(td);
      });

      // Row click handler for selection
      tr.addEventListener('click', (e) => {
        if ((e.target as HTMLInputElement).type !== 'checkbox') {
          this.handleRowClick(originalIndex, e.ctrlKey || e.metaKey);
        }
      });

      // Row hover handlers for tree tip highlighting
      tr.addEventListener('mouseenter', () => {
        this.handleRowHover(originalIndex);
      });

      tr.addEventListener('mouseleave', () => {
        this.handleRowHoverEnd();
      });

      tbody.appendChild(tr);
    });

    this.updateSelectionInfo();
    this.updateSelectAllCheckbox();
    this.updateMappingInfo();
  }

  /**
   * Add clear selection button to tree container
   */
  addClearSelectionButton(container: HTMLElement): void {
    // Check if button already exists
    let clearBtn = document.getElementById('clear-selection-btn-tree') as HTMLButtonElement | null;

    if (!clearBtn) {
      // Create button if it doesn't exist
      clearBtn = document.createElement('button');
      clearBtn.id = 'clear-selection-btn-tree';
      clearBtn.className = 'btn-clear-selection-tree';
      clearBtn.title = 'Clear selection';
      clearBtn.style.display = 'none';

      clearBtn.innerHTML = `
        <span class="clear-selection-icon">✖</span>
        <span class="clear-selection-label">Clear Selection</span>
      `;

      // Add click handler
      clearBtn.addEventListener('click', () => {
        this.clearSelection();
      });

      container.appendChild(clearBtn);
      console.log('[TheiaPhyloViewer] Clear selection button added to tree container');
    }
  }

  /**
   * Build the shared appearance panel for renderers that have no appearance UI
   * of their own. Phylocanvas ships its own gear-menu, so the visibility policy
   * skips it (no competing double UI); every other renderer gets these controls.
   * Rebuilt on each render (the tree-container is cleared first), it restores any
   * saved appearance and persists every change through the existing settings.
   */
  setupAppearancePanel(container: HTMLElement): void {
    if (rendererProvidesOwnAppearanceUI(this.rendererType as RendererType)) {
      return;
    }

    // Metadata field names in the current data; drives the metadata-block and
    // colour-by sections below and prunes stale saved selections on restore.
    const metadataFields =
      this.metadata && this.metadata.length > 0 && this.metadata[0]
        ? Object.keys(this.metadata[0])
        : [];
    this.migrateLegacyAppearance();
    const canonical = this.loadCanonicalAppearanceBag();
    const state = canonicalToAppearance(canonical, metadataFields);
    // Only push onto a renderer the user has actually customised, so a fresh
    // install's first render stays byte-for-byte at the renderer's own defaults.
    if (canonicalHasAppearance(canonical)) {
      applyAppearanceToRenderer(this.treeRenderer, state);
    }
    const persist = (): void => this.saveSettings({ phylocanvas: appearanceToCanonical(state) });

    const wrap = document.createElement('div');
    wrap.className = 'appearance-panel-wrap';

    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'appearance-toggle';
    toggleBtn.title = 'Appearance';
    toggleBtn.setAttribute('aria-label', 'Appearance settings');
    toggleBtn.textContent = '⚙';

    const panel = document.createElement('div');
    panel.className = 'appearance-panel hidden';
    toggleBtn.addEventListener('click', () => panel.classList.toggle('hidden'));

    const header = document.createElement('div');
    header.className = 'appearance-panel-header';
    header.textContent = 'Appearance';

    const body = document.createElement('div');
    body.className = 'appearance-panel-body';

    const addRow = (labelText: string): HTMLDivElement => {
      const row = document.createElement('div');
      row.className = 'appearance-row';
      const span = document.createElement('span');
      span.textContent = labelText;
      row.appendChild(span);
      body.appendChild(row);
      return row;
    };

    const layoutRow = addRow('Layout');
    const layoutSelect = document.createElement('select');
    for (const { code, label } of APPEARANCE_LAYOUTS) {
      const opt = document.createElement('option');
      opt.value = code;
      opt.textContent = label;
      layoutSelect.appendChild(opt);
    }
    layoutSelect.value = state.layout;
    layoutSelect.addEventListener('change', () => {
      state.layout = layoutSelect.value as typeof state.layout;
      this.treeRenderer?.changeTreeLayout?.(state.layout);
      persist();
    });
    layoutRow.appendChild(layoutSelect);

    const shapeRow = addRow('Node shape');
    const shapeSelect = document.createElement('select');
    for (const { code, label } of APPEARANCE_NODE_SHAPES) {
      const opt = document.createElement('option');
      opt.value = code;
      opt.textContent = label;
      shapeSelect.appendChild(opt);
    }
    shapeSelect.value = state.nodeShape;
    shapeSelect.addEventListener('change', () => {
      state.nodeShape = shapeSelect.value as typeof state.nodeShape;
      this.treeRenderer?.setNodeShape?.(state.nodeShape);
      persist();
    });
    shapeRow.appendChild(shapeSelect);

    const fontRow = addRow('Label font');
    const fontSelect = document.createElement('select');
    for (const { code, label } of APPEARANCE_FONT_FAMILIES) {
      const opt = document.createElement('option');
      opt.value = code;
      opt.textContent = label;
      fontSelect.appendChild(opt);
    }
    fontSelect.value = state.fontFamily;
    fontSelect.addEventListener('change', () => {
      state.fontFamily = fontSelect.value as typeof state.fontFamily;
      this.treeRenderer?.setFontFamily?.(state.fontFamily);
      persist();
    });
    fontRow.appendChild(fontSelect);

    const colourRow = addRow('Label colour');
    const colourInput = document.createElement('input');
    colourInput.type = 'color';
    colourInput.value = state.fontColour;
    colourInput.addEventListener('change', () => {
      state.fontColour = colourInput.value;
      this.treeRenderer?.setFontColour?.(state.fontColour);
      persist();
    });
    colourRow.appendChild(colourInput);

    const addToggle = (
      labelText: string,
      checked: boolean,
      apply: (on: boolean) => void,
    ): void => {
      const row = addRow(labelText);
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'toggle-switch';
      input.checked = checked;
      input.addEventListener('change', () => {
        apply(input.checked);
        persist();
      });
      row.appendChild(input);
    };

    addToggle('Leaf labels', state.leafLabels, (on) => {
      state.leafLabels = on;
      this.treeRenderer?.toggleLeafLabels?.(on);
    });
    addToggle('Align labels', state.alignLabels, (on) => {
      state.alignLabels = on;
      this.treeRenderer?.toggleLabelAlignment?.(on);
    });
    addToggle('Internal labels', state.internalLabels, (on) => {
      state.internalLabels = on;
      this.treeRenderer?.toggleInternalLabels?.(on);
    });
    addToggle('Colour edges', state.colourEdges, (on) => {
      state.colourEdges = on;
      this.treeRenderer?.toggleColourEdges?.(on);
    });
    addToggle('Leaf borders', state.leafBorders, (on) => {
      state.leafBorders = on;
      this.treeRenderer?.toggleLeafBorders?.(on);
    });
    addToggle('Scalebar', state.scalebar, (on) => {
      state.scalebar = on;
      this.treeRenderer?.toggleScalebar?.(on);
    });
    addToggle('Pie charts', state.showPiecharts, (on) => {
      state.showPiecharts = on;
      this.treeRenderer?.togglePiecharts?.(on);
    });

    const addSlider = (
      labelText: string,
      value: number,
      range: { min: number; max: number },
      apply: (n: number) => void,
      opts: { step?: number; format?: (n: number) => string } = {},
    ): void => {
      const format = opts.format ?? String;
      const row = document.createElement('div');
      row.className = 'appearance-row appearance-slider';
      const head = document.createElement('div');
      head.className = 'appearance-slider-head';
      const span = document.createElement('span');
      span.textContent = labelText;
      const valueSpan = document.createElement('span');
      valueSpan.className = 'appearance-slider-value';
      valueSpan.textContent = format(value);
      head.append(span, valueSpan);
      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(range.min);
      input.max = String(range.max);
      if (opts.step) input.step = String(opts.step);
      input.value = String(value);
      input.addEventListener('input', () => {
        const n = Number(input.value);
        valueSpan.textContent = format(n);
        apply(n);
      });
      input.addEventListener('change', persist);
      row.append(head, input);
      body.appendChild(row);
    };

    addSlider('Node size', state.nodeSize, NODE_SIZE_RANGE, (n) => {
      state.nodeSize = n;
      this.treeRenderer?.setNodeSize?.(n);
    });
    addSlider('Label size', state.labelSize, LABEL_SIZE_RANGE, (n) => {
      state.labelSize = n;
      this.treeRenderer?.setLabelSize?.(n);
    });
    addSlider('Internal label size', state.internalLabelSize, LABEL_SIZE_RANGE, (n) => {
      state.internalLabelSize = n;
      this.treeRenderer?.setInternalLabelSize?.(n);
    });

    // Numeric value filter for internal labels (e.g. bootstrap support): two
    // number inputs forming one logical range. The renderer applies it only when
    // *both* bounds are set, so a single blank field clears the filter.
    const filterRow = addRow('Internal label range');
    const numberToField = (value: number | null): string => (value == null ? '' : String(value));
    const fieldToNumber = (raw: string): number | null => {
      const n = Number(raw);
      return raw.trim() !== '' && Number.isFinite(n) ? n : null;
    };
    const applyFilter = (): void => {
      this.treeRenderer?.setInternalLabelFilter?.(state.internalLabelMin, state.internalLabelMax);
      persist();
    };
    const makeBoundInput = (
      placeholder: string,
      initial: number | null,
      assign: (value: number | null) => void,
    ): void => {
      const input = document.createElement('input');
      input.type = 'number';
      input.className = 'appearance-range-input';
      input.placeholder = placeholder;
      input.value = numberToField(initial);
      input.addEventListener('change', () => {
        assign(fieldToNumber(input.value));
        applyFilter();
      });
      filterRow.appendChild(input);
    };
    makeBoundInput('min', state.internalLabelMin, (v) => {
      state.internalLabelMin = v;
    });
    makeBoundInput('max', state.internalLabelMax, (v) => {
      state.internalLabelMax = v;
    });

    // Highlight/selection halo styling. The colour picker mirrors the label
    // colour above (hex stored, converted to RGBA at the renderer boundary); the
    // two sliders size the ring radius and stroke. All three persist.
    const haloColourRow = addRow('Halo colour');
    const haloColourInput = document.createElement('input');
    haloColourInput.type = 'color';
    haloColourInput.value = state.haloColour;
    haloColourInput.addEventListener('change', () => {
      state.haloColour = haloColourInput.value;
      this.treeRenderer?.setHaloColour?.(state.haloColour);
      persist();
    });
    haloColourRow.appendChild(haloColourInput);
    addSlider('Halo radius', state.haloRadius, HALO_RADIUS_RANGE, (n) => {
      state.haloRadius = n;
      this.treeRenderer?.setHaloRadius?.(n);
    });
    addSlider('Halo width', state.haloWidth, HALO_WIDTH_RANGE, (n) => {
      state.haloWidth = n;
      this.treeRenderer?.setHaloWidth?.(n);
    });
    addSlider('Padding', state.padding, PADDING_RANGE, (n) => {
      state.padding = n;
      this.treeRenderer?.setPadding?.(n);
    });
    addSlider('Block size', state.blockSize, BLOCK_SIZE_RANGE, (n) => {
      state.blockSize = n;
      this.treeRenderer?.setBlockSize?.(n);
    });

    // Scalebar customisation (a curated subset of the library's scalebar props-// the corner position is omitted because renderScalebar honours only the
    // left/bottom offsets). The colour picker sets one "ink" for both the bar
    // line and the value label (renderer binds fill + stroke together); the
    // opacity slider reads out as a percentage. All persist.
    addSlider('Scalebar digits', state.scalebarDigits, SCALEBAR_DIGITS_RANGE, (n) => {
      state.scalebarDigits = n;
      this.treeRenderer?.setScalebarDigits?.(n);
    });
    addSlider('Scalebar width', state.scalebarWidth, SCALEBAR_WIDTH_RANGE, (n) => {
      state.scalebarWidth = n;
      this.treeRenderer?.setScalebarWidth?.(n);
    });
    addSlider('Scalebar font size', state.scalebarFontSize, SCALEBAR_FONT_SIZE_RANGE, (n) => {
      state.scalebarFontSize = n;
      this.treeRenderer?.setScalebarFontSize?.(n);
    });
    addSlider(
      'Scalebar opacity',
      state.scalebarOpacity,
      SCALEBAR_OPACITY_RANGE,
      (n) => {
        state.scalebarOpacity = n;
        this.treeRenderer?.setScalebarOpacity?.(n);
      },
      { step: 0.05, format: (v) => `${Math.round(v * 100)}%` },
    );
    const scalebarColourRow = addRow('Scalebar colour');
    const scalebarColourInput = document.createElement('input');
    scalebarColourInput.type = 'color';
    scalebarColourInput.value = state.scalebarColour;
    scalebarColourInput.addEventListener('change', () => {
      state.scalebarColour = scalebarColourInput.value;
      this.treeRenderer?.setScalebarColour?.(state.scalebarColour);
      persist();
    });
    scalebarColourRow.appendChild(scalebarColourInput);

    // Independent branch-axis and step-axis (leaf-spacing) stretch. Unlike the
    // uniform Zoom below, these set library *props* (setBranchZoom/setStepZoom →
    // setProps), so they describe the tree's layout and *do* persist; hence the
    // standard addSlider with its built-in change→persist. The fractional log2
    // step and "×" read-out come from the optional step/format hooks.
    addSlider(
      'Branch zoom',
      state.branchZoom,
      BRANCH_ZOOM_RANGE,
      (n) => {
        state.branchZoom = n;
        this.treeRenderer?.setBranchZoom?.(n);
      },
      { step: 0.1, format: formatZoomMultiplier },
    );
    addSlider(
      'Step zoom',
      state.stepZoom,
      STEP_ZOOM_RANGE,
      (n) => {
        state.stepZoom = n;
        this.treeRenderer?.setStepZoom?.(n);
      },
      { step: 0.1, format: formatZoomMultiplier },
    );

    // Uniform zoom relative to fit. Transient camera state, not appearance, so
    // this slider drives the renderer live but deliberately does NOT persist()
    // and is rebuilt at fit (zoom 0) on each render. Fractional log2 step and a
    // "×" multiplier read-out set it apart from the integer size sliders, so it
    // is built inline rather than through `addSlider`. Only offered when the
    // renderer exposes the camera methods.
    if (this.treeRenderer?.setZoom && this.treeRenderer?.getZoom) {
      const row = document.createElement('div');
      row.className = 'appearance-row appearance-slider';
      const head = document.createElement('div');
      head.className = 'appearance-slider-head';
      const span = document.createElement('span');
      span.textContent = 'Zoom';
      const valueSpan = document.createElement('span');
      valueSpan.className = 'appearance-slider-value';
      const initial = Number(this.treeRenderer.getZoom()) || 0;
      valueSpan.textContent = formatZoomMultiplier(initial);
      head.append(span, valueSpan);
      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(ZOOM_RANGE.min);
      input.max = String(ZOOM_RANGE.max);
      input.step = '0.1';
      input.value = String(initial);
      input.addEventListener('input', () => {
        const z = Number(input.value);
        valueSpan.textContent = formatZoomMultiplier(z);
        this.treeRenderer?.setZoom?.(z);
      });
      row.append(head, input);
      body.appendChild(row);
    }

    // Metadata blocks, SVG-only parity with phylocanvas's embedded block menu. A
    // checkbox per metadata field; ticking paints that field's coloured heat-map
    // column beside the tips, unticking clears it. Blocks come from the same pure
    // planner the Tableau load path uses, so colours and hide-null match exactly.
    // `colorByField` is left out of the planner bag so the colour-by section below
    // remains the sole owner of leaf-marker colouring.
    if (metadataFields.length > 0 && this.treeRenderer?.setMetadataBlocks) {
      addRow('Metadata blocks');
      // The block columns' value->swatch legend is a floating card on the tree
      // canvas, reusing the renderer-agnostic overlay the Tableau load path drives.
      // A fresh instance per render matches the colour-by card's local-rebuild
      // contract -- renderTree() has already blanked the container before this runs,
      // and update() removes its own prior card, so checkbox toggles repaint cleanly.
      // Clicking a block-legend swatch selects every tip with that value, the
      // same parity the colour-by legend gets, routed through the shared
      // renderer-agnostic selection path.
      const metadataLegend = new MetadataLegend((field, value) => {
        const ids = TreeMetadataSync.labelsForFieldValue(field, value);
        if (ids.length > 0) this.handleLassoSelection(ids, 'legend');
      });
      const applyBlocks = (): void => {
        const plan = planMetadataDispatch(
          {
            selectedMetadataFields: state.selectedMetadataFields,
            fieldPalettes: canonical.fieldPalettes,
          },
          TreeMetadataSync,
          { metadata: this.metadata, columnTypes: this.columnTypes },
        );
        this.treeRenderer?.setMetadataBlocks?.(plan.fields, plan.blocks);
        // Pinned bottom-right -- the free corner, since colour-by owns top-left, the
        // gear panel top-right, and the scale bar bottom-left. An empty selection
        // yields an empty model, so the overlay removes its own card.
        metadataLegend.update(
          plan.legend ? { ...plan.legend, position: 'bottom-right' } : null,
          plan.fields,
          plan.blocks,
        );
      };
      for (const field of metadataFields) {
        const fieldRow = addRow(field);
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = state.selectedMetadataFields.includes(field);
        checkbox.addEventListener('change', () => {
          const selected = new Set(state.selectedMetadataFields);
          if (checkbox.checked) {
            selected.add(field);
          } else {
            selected.delete(field);
          }
          // Rebuild in field order so the block columns keep a stable order
          // regardless of the order the user ticked them.
          state.selectedMetadataFields = metadataFields.filter((f) => selected.has(f));
          applyBlocks();
          persist();
        });
        fieldRow.appendChild(checkbox);
      }
      if (state.selectedMetadataFields.length > 0) applyBlocks();
    }

    // Colour-by leaf markers, SVG-only parity with phylocanvas's "Color By". The
    // dropdown lists metadata fields; the legend below it maps each value to its
    // swatch. Only built when metadata is present and the renderer can consume it.
    if (metadataFields.length > 0 && this.treeRenderer?.setLeafColorMap) {
      const colorByRow = addRow('Colour by');
      const colorBySelect = document.createElement('select');
      const noneOpt = document.createElement('option');
      noneOpt.value = '';
      noneOpt.textContent = 'None';
      colorBySelect.appendChild(noneOpt);
      for (const field of metadataFields) {
        const opt = document.createElement('option');
        opt.value = field;
        opt.textContent = field;
        colorBySelect.appendChild(opt);
      }
      colorByRow.appendChild(colorBySelect);

      // The value-to-swatch legend is a floating card on the tree canvas (a
      // sibling overlay of the SVG), not a panel row: it reuses the
      // metadata-legend overlay chrome and is pinned top-left -- the free
      // corner, since the gear panel and clear-selection button own top-right
      // and the scale bar owns bottom-left. renderTree() blanks the container
      // before this runs, so the card follows the same self-build-per-render
      // contract as the panel: applyColorBy rebuilds it from the restored field
      // on every render.
      let colourLegendCard: HTMLElement | null = null;

      const applyColorBy = (field: string | null): void => {
        colourLegendCard?.remove();
        colourLegendCard = null;
        if (!field) {
          this.treeRenderer?.setLeafColorMap?.(null);
          return;
        }
        // Resolve the persisted colour-by palette the same way phylocanvas does on
        // its own load, so a switch from phylocanvas keeps the chosen palette instead
        // of snapping leaves back to Set1. The category is always qualitative here.
        TreeMetadataSync.setLeafPalette('qualitative', resolveColorByPaletteName(canonical, field));
        const valueColors = TreeMetadataSync.generateLeafColorMap(
          this.metadata,
          field,
          this.columnTypes,
        );
        const labelField = TreeMetadataSync.state.labelField || 'id';
        this.treeRenderer?.setLeafColorMap?.(
          buildLeafColorMap(this.metadata, labelField, field, valueColors),
        );

        const card = document.createElement('div');
        card.className = 'colour-by-legend legend-pos-top-left';
        const section = document.createElement('div');
        section.className = 'metadata-legend-section';
        const heading = document.createElement('div');
        heading.className = 'metadata-legend-field';
        heading.textContent = field;
        section.appendChild(heading);
        const entries = document.createElement('div');
        entries.className = 'metadata-legend-entries';
        for (const { value, colour } of legendEntries(valueColors)) {
          const entry = document.createElement('div');
          entry.className = 'metadata-legend-entry';
          const swatch = document.createElement('span');
          swatch.className = 'metadata-legend-swatch';
          swatch.style.backgroundColor = colour;
          const label = document.createElement('span');
          label.className = 'metadata-legend-label';
          label.textContent = value;
          label.title = value;
          entry.append(swatch, label);
          // Click a swatch to select every tip with that value -- parity with
          // phylocanvas's clickable legend, routed through the same renderer-
          // agnostic selection path so the SVG halo, table rows and count update
          // identically.
          entry.style.cursor = 'pointer';
          entry.title = `Select tips where ${field} = ${value}`;
          entry.addEventListener('click', () => {
            const ids = TreeMetadataSync.labelsForFieldValue(field, value);
            if (ids.length > 0) this.handleLassoSelection(ids, 'legend');
          });
          entries.appendChild(entry);
        }
        section.appendChild(entries);
        card.appendChild(section);
        container.appendChild(card);
        colourLegendCard = card;
      };

      const restoredField = metadataFields.includes(state.colorByField ?? '')
        ? state.colorByField
        : null;
      colorBySelect.value = restoredField ?? '';
      colorBySelect.addEventListener('change', () => {
        state.colorByField = colorBySelect.value || null;
        applyColorBy(state.colorByField);
        persist();
      });
      if (restoredField) applyColorBy(restoredField);
    }

    panel.append(header, body);
    wrap.append(toggleBtn, panel);
    container.appendChild(wrap);
  }

  /**
   * Render phylogenetic tree
   */
  renderTree(): void {
    const container = document.getElementById('tree-container') as HTMLElement;

    try {
      // Clear container before rendering new tree
      container.innerHTML = '';

      // Destroy previous renderer if exists
      if (this.treeRenderer && typeof this.treeRenderer.destroy === 'function') {
        this.treeRenderer.destroy();
      }

      // Legend colour overrides must be in TreeMetadataSync before the renderer
      // below generates its first colour maps (colour-by restore, metadata blocks).
      this.restoreCustomColors();

      // Choose renderer based on type. rendererType is sanitized on load, so the
      // final branch is a defensive backstop for an unexpected runtime value.
      if (this.rendererType === 'phylocanvas') {
        this.treeRenderer = new PhylocanvasRenderer(container, this.tree as string, this);
      } else if (this.rendererType === 'theiaphyloviewer') {
        this.treeRenderer = new TheiaPhyloViewerRenderer(container, this.tree as string, this);
      } else {
        console.warn(`Unknown rendererType "${this.rendererType}", falling back to TheiaPhyloViewer`);
        this.treeRenderer = new TheiaPhyloViewerRenderer(container, this.tree as string, this);
      }

      this.treeRenderer.render();

      // Add clear selection button to tree container
      this.addClearSelectionButton(container);

      // Shared appearance panel for renderers without their own embedded UI
      // (phylocanvas ships its own gear-menu, so it is skipped). Rebuilt each
      // render because the tree-container was cleared above.
      this.setupAppearancePanel(container);

      // Update phylocanvas menu with metadata fields
      if (this.rendererType === 'phylocanvas' && this.metadata && this.metadata.length > 0 && this.metadata[0]) {
        const metadataFields = Object.keys(this.metadata[0]!);
        this.treeRenderer.updateMenuWithMetadata(metadataFields);

        // Setup color by controls and restore settings
        this.setupColorByControls();
        this.restoreColorBySettings();

        // Setup leaf text control
        this.setupTipLabelControl();
      }

      // Tree node selection handler
      this.treeRenderer.addListener('click', (event: any) => {
        if (event.node) {
          if (event.node.isLeaf) {
            // Leaf node clicked - highlight in table
            this.handleTreeNodeClick(event.node.id, event.node);
          } else {
            // Internal node (clade) clicked - call handleTreeNodeClick with full node
            // Tableau extension will handle clade filtering
            this.handleTreeNodeClick(event.node.id, event.node);
          }
        }
      });

      // Lasso selection handler (also handles legend selection)
      this.treeRenderer.addListener('lasso-select', (event: any) => {
        if (event.leafIds && event.leafIds.length > 0) {
          this.handleLassoSelection(event.leafIds, event.source || 'lasso');
        }
      });

      // Metadata blocks change handler (for phylocanvas)
      // Note: Metadata blocks and leaf coloring are independent - no sync between them
      this.treeRenderer.addListener('metadataBlocksChange', (event: any) => {
        if (event.fields && event.fields.length > 0) {
          // Generate color maps for each field with field-specific palettes
          event.fields.forEach((field: string) => {
            // Get field-specific palette from renderer or use default
            const paletteName = this.treeRenderer.fieldPalettes?.get(field) || 'Set1';

            // Set palette for this specific field
            TreeMetadataSync.currentPaletteCategory = 'qualitative';
            TreeMetadataSync.currentPaletteName = paletteName;

            // Always regenerate color map to ensure correct palette is used
            console.log(`[TheiaPhyloViewer] Generating color map for field "${field}" with palette "${paletteName}"`);
            TreeMetadataSync.generateColorMap(this.metadata, field, this.columnTypes);
          });

          // Sync null block visibility setting
          TreeMetadataSync.state.hideNullBlocks = this.treeRenderer.hideNullBlocks;

          // Render metadata blocks (independent of leaf coloring)
          const metadata = TreeMetadataSync.computeMetadataBlocks(event.fields);
          console.log('[TheiaPhyloViewer] Computed metadata blocks:', metadata);
          console.log('[TheiaPhyloViewer] Fields:', event.fields);
          this.treeRenderer.setMetadataBlocks(event.fields, metadata);
        } else {
          // No fields selected - clear metadata blocks only (leave leaf colors unchanged)
          this.treeRenderer.setMetadataBlocks([], {});
        }
      });

      // Subtree view handler - filter table to show only subtree tips
      this.treeRenderer.addListener('subtreeView', (event: any) => {
        console.log('[TheiaPhyloViewer] Subtree view activated with', event.leafIds.length, 'tips');
        this.filteredTipIds = event.leafIds;
        this.renderTable();
      });

      // Reset view handler - show all table rows
      this.treeRenderer.addListener('resetView', () => {
        console.log('[TheiaPhyloViewer] View reset - showing all table rows');
        this.filteredTipIds = null;
        this.renderTable();
      });

      // Handle window resize (only add once)
      if (!this._resizeHandlerAdded) {
        window.addEventListener('resize', () => {
          if (this.treeRenderer) {
            this.treeRenderer.resize();
          }
        });
        this._resizeHandlerAdded = true;
      }

      // Restore a persisted metadata-block selection when this render switched
      // into phylocanvas. The draw path runs through the metadataBlocksChange
      // listener attached above, so this must follow every addListener call.
      this.restorePhylocanvasMetadataBlocks();

      // Restore tooltip-field selection (both renderers) and phylocanvas axis zoom.
      // Runs after the appearance panel and phylocanvas menu are built so the SVG
      // setter and the phylocanvas tooltip checkboxes both exist.
      this.restoreTooltipAndZoomSettings();

      console.log(`[TheiaPhyloViewer] Tree rendered successfully using ${this.rendererType} renderer`);
    } catch (error: any) {
      console.error('Failed to render tree:', error);
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = 'padding: 20px; text-align: center;';
      errorDiv.textContent = `Failed to render tree: ${error.message}`;
      container.replaceChildren(errorDiv);
    }
  }

  /**
   * Update tree node colors
   */
  updateTreeColors(): void {
    if (!this.treeRenderer) return;

    console.log('[DEBUG] updateTreeColors:');
    console.log('  rowsByLabel.size:', TreeMetadataSync.state.rowsByLabel.size);
    console.log('  colorByField:', TreeMetadataSync.state.colorByField);
    console.log('  colorMap.size:', TreeMetadataSync.state.colorMap.size);
    console.log('  colorByEnabled:', TreeMetadataSync.state.colorByEnabled);

    try {
      const nodeStyles = TreeMetadataSync.computeNodeStyles(this.metadata);
      console.log('  nodeStyles count:', Object.keys(nodeStyles).length);
      console.log('[TheiaPhyloViewer] Computed node styles:', nodeStyles);
      console.log('[TheiaPhyloViewer] Color map:', TreeMetadataSync.state.colorMap);
      this.treeRenderer.setNodeStyles(nodeStyles);
    } catch (error) {
      console.error('Failed to update tree colors:', error);
    }
  }

  /**
   * Reset tree node colors to default
   */
  resetTreeColors(): void {
    if (!this.treeRenderer) return;

    try {
      // Clear color state in TreeMetadataSync
      TreeMetadataSync.state.colorByField = null;
      TreeMetadataSync.state.leafColorByField = null;
      TreeMetadataSync.state.colorMap = new Map();
      TreeMetadataSync.state.leafColorMap = new Map();
      TreeMetadataSync.setColorByEnabled(false);

      // Reset node styles to default (empty styles)
      this.treeRenderer.setNodeStyles({});
      console.log('[TheiaPhyloViewer] Tree colors reset to default');
    } catch (error) {
      console.error('Failed to reset tree colors:', error);
    }
  }

  /**
   * Show palette selection menu
   */
  showPaletteMenu(triggerElement: HTMLElement, field: string): void {
    const paletteMenu = document.getElementById('palette-menu') as HTMLElement | null;
    const paletteContent = document.getElementById('palette-menu-content') as HTMLElement | null;

    if (!paletteMenu || !paletteContent) return;

    // Get available palettes
    const palettes = ColorBrewerPalettes.qualitative || {};

    const currentPalette = TreeMetadataSync.currentPaletteName;

    // Build palette items HTML
    let paletteHTML = '<div class="palette-category">Qualitative Palettes</div>';

    Object.keys(palettes).forEach(paletteName => {
      const palette = palettes[paletteName] as any;
      const availableSizes = Object.keys(palette).map(Number).sort((a: number, b: number) => a - b);
      const preferredSize = availableSizes.find((s: number) => s >= 8) ?? availableSizes[availableSizes.length - 1]!;
      const paletteColors = palette[preferredSize]?.slice(0, 8) || [];
      const isSelected = paletteName === currentPalette;

      paletteHTML += `
        <div class="palette-item ${isSelected ? 'selected' : ''}" data-palette="${paletteName}">
          <div class="palette-name">${paletteName}</div>
          <div class="palette-preview">
            ${paletteColors.map((color: string) => `<div class="palette-color" style="background-color: ${color}"></div>`).join('')}
          </div>
        </div>
      `;
    });

    paletteContent.innerHTML = paletteHTML;

    // Show menu temporarily to measure its dimensions
    paletteMenu.classList.remove('hidden');
    paletteMenu.style.visibility = 'hidden';

    // Get button and menu dimensions
    const rect = triggerElement.getBoundingClientRect();
    const menuRect = paletteMenu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate available space
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const spaceRight = viewportWidth - rect.left;

    // Determine vertical position (prefer below, but use above if not enough space)
    let top: number;
    if (spaceBelow >= menuRect.height + 10 || spaceBelow > spaceAbove) {
      // Position below
      top = rect.bottom + 5;
    } else {
      // Position above
      top = rect.top - menuRect.height - 5;
    }

    // Determine horizontal position (prefer left-aligned, but adjust if needed)
    let left = rect.left;
    if (left + menuRect.width > viewportWidth) {
      // Align to right edge of button
      left = rect.right - menuRect.width;
      // If still off-screen, align to viewport edge
      if (left < 10) {
        left = viewportWidth - menuRect.width - 10;
      }
    }

    // Ensure menu doesn't go above viewport
    if (top < 10) {
      top = 10;
    }

    // Ensure menu doesn't go below viewport
    if (top + menuRect.height > viewportHeight - 10) {
      top = viewportHeight - menuRect.height - 10;
    }

    // Apply calculated position
    paletteMenu.style.top = `${top}px`;
    paletteMenu.style.left = `${left}px`;
    paletteMenu.style.visibility = 'visible';

    // Suppress unused variable warning for spaceRight (kept for symmetry with original)
    void spaceRight;

    // Add click handlers to palette items
    const paletteItems = paletteContent.querySelectorAll('.palette-item');
    paletteItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent closing the main settings panel
        const paletteName = (item as HTMLElement).dataset['palette']!;
        this.applyPalette(field, 'qualitative', paletteName);
        paletteMenu.classList.add('hidden');
      });
    });

    // Close menu when clicking outside
    const closeMenu = (e: MouseEvent) => {
      if (!paletteMenu.contains(e.target as Node) && e.target !== triggerElement) {
        paletteMenu.classList.add('hidden');
        document.removeEventListener('click', closeMenu);
      }
    };

    setTimeout(() => {
      document.addEventListener('click', closeMenu);
    }, 0);
  }

  /**
   * Apply palette to a field
   */
  applyPalette(field: string, category: string, paletteName: string): void {
    console.log(`[TheiaPhyloViewer] Applying palette "${paletteName}" to field "${field}"`);

    // Update TreeMetadataSync - use LEAF-SPECIFIC palette and color map
    TreeMetadataSync.setLeafPalette(category, paletteName);
    TreeMetadataSync.generateLeafColorMap(this.metadata, field, this.columnTypes);
    this.updateTreeColors();

    // Update the palette in the renderer's state to keep in sync
    if (this.treeRenderer) {
      if (this.treeRenderer.fieldPalettes) {
        this.treeRenderer.fieldPalettes.set(field, paletteName);
      }
      // Also update the colorByPalette property for the current field
      this.treeRenderer.colorByPalette = { category, name: paletteName };
      // Update legend to reflect new colors
      this.treeRenderer.updateLegend(
        this.treeRenderer.metadataBlocks || [],
        this.treeRenderer.metadata || {}
      );
    }

    // Save palette preference into the canonical phylocanvas bag.
    this.saveSettings({
      phylocanvas: { colorByPalette: { category, name: paletteName } }
    });

    // Update the palette preview
    this.updateLeafColorPalettePreview(paletteName);
  }

  /**
   * Update the leaf color palette preview display
   */
  updateLeafColorPalettePreview(paletteName: string | null): void {
    const previewDiv = document.getElementById('leafColorPalettePreview') as HTMLElement | null;
    if (!previewDiv) return;

    // Clear existing content
    previewDiv.textContent = '';

    if (!paletteName) {
      previewDiv.style.display = 'none';
      return;
    }

    // Get palette colors
    const palettes = ColorBrewerPalettes.qualitative || {};
    const paletteObj = palettes[paletteName];
    if (!paletteObj) {
      previewDiv.style.display = 'none';
      return;
    }

    const availableSizes = Object.keys(paletteObj).map(Number).sort((a: number, b: number) => a - b);
    const preferredSize = availableSizes.find((s: number) => s >= 8) ?? availableSizes[availableSizes.length - 1]!;
    const paletteColors = paletteObj[preferredSize]?.slice(0, 8) || [];

    // Build preview using safe DOM methods
    paletteColors.forEach((color: string) => {
      const colorDiv = document.createElement('div');
      colorDiv.className = 'palette-color';
      colorDiv.style.backgroundColor = color;
      previewDiv.appendChild(colorDiv);
    });
    previewDiv.style.display = 'flex';
  }

  /**
   * Update tree tip labels based on selected display field
   */
  updateTreeTipLabels(displayField: string): void {
    if (!this.treeRenderer) return;

    try {
      const labelMap: Record<string, string> = {};

      if (displayField) {
        // Store display field
        this.displayAsField = displayField;

        // Helper to check if value is empty
        const isEmptyValue = (value: any): boolean => {
          if (value === null || value === undefined || value === '') {
            return true;
          }
          const strValue = String(value).trim().toLowerCase();
          return strValue === 'null' || strValue === 'n/a' || strValue === 'na' || strValue === '%null%';
        };

        // Build label mapping: original label -> display label
        TreeMetadataSync.state.rowsByLabel.forEach((rowData: any, originalLabel: string) => {
          if (rowData && rowData.length > 0) {
            const row = rowData[0].row;
            const displayValue = row[displayField];

            // Store original label if not already stored
            if (!this.originalTipLabels.has(originalLabel)) {
              this.originalTipLabels.set(originalLabel, originalLabel);
            }

            if (isEmptyValue(displayValue)) {
              // Show blank for empty values
              labelMap[originalLabel] = '';
            } else {
              labelMap[originalLabel] = String(displayValue);
            }
          }
        });

        console.log('[TheiaPhyloViewer] Updated tree tip labels with field:', displayField);
        console.log('[TheiaPhyloViewer] Label map sample:', Object.entries(labelMap).slice(0, 5));
      } else {
        // Reset to original labels
        this.displayAsField = null;
        this.originalTipLabels.forEach((originalLabel, key) => {
          labelMap[key] = originalLabel;
        });
        console.log('[TheiaPhyloViewer] Reset tree tip labels to original');
      }

      // Apply label updates to renderer
      if (this.treeRenderer.updateTipLabels) {
        this.treeRenderer.updateTipLabels(labelMap);
      }
    } catch (error) {
      console.error('Failed to update tree tip labels:', error);
    }
  }

  /**
   * Update metadata blocks on tree
   */
  updateMetadataBlocks(show: boolean): void {
    if (!this.treeRenderer) return;

    if (
      ['phylocanvas', 'theiaphyloviewer'].includes(this.rendererType) &&
      show &&
      TreeMetadataSync.state.colorByField
    ) {
      TreeMetadataSync.state.hideNullBlocks = this.treeRenderer.hideNullBlocks;
      const fields = [TreeMetadataSync.state.colorByField];
      const metadata = TreeMetadataSync.computeMetadataBlocks(fields);
      this.treeRenderer.setMetadataBlocks(fields, metadata);
    } else if (['phylocanvas', 'theiaphyloviewer'].includes(this.rendererType)) {
      this.treeRenderer.setMetadataBlocks([], {});
    } else {
      // Defensive: rendererType is sanitized to a supported renderer on load.
      console.log('[TheiaPhyloViewer] Metadata blocks skipped for unsupported renderer:', this.rendererType);
    }
  }

  /**
   * Toggle branch length display on tree
   */
  toggleBranchLengths(show: boolean): void {
    if (!this.treeRenderer) return;

    try {
      if (this.treeRenderer.toggleBranchLengths) {
        this.treeRenderer.toggleBranchLengths(show);
        console.log('[TheiaPhyloViewer] Branch lengths', show ? 'shown' : 'hidden');
      }
    } catch (error) {
      console.error('Failed to toggle branch lengths:', error);
    }
  }

  /**
   * Handle row click in table
   */
  handleRowClick(rowIndex: number, multiSelect: boolean): void {
    if (!multiSelect) {
      this.selectedRows.clear();
    }

    if (this.selectedRows.has(rowIndex)) {
      this.selectedRows.delete(rowIndex);
    } else {
      this.selectedRows.add(rowIndex);
    }

    // Update table UI
    this.updateTableSelection();

    // Sync to tree
    const leafIds = TreeMetadataSync.rowSelectionsToTreeLeaves(
      Array.from(this.selectedRows)
    );
    this.highlightTreeNodes(leafIds);

    this.updateSelectionInfo();

    // Zoom to tip if single row selected (not multi-select)
    if (!multiSelect && this.selectedRows.size === 1 && leafIds.length > 0) {
      // Zoom to the first leaf (usually there's only one per row)
      this.zoomToTip(leafIds[0]!);
    }
  }

  /**
   * Zoom to a specific tip in the tree
   */
  zoomToTip(leafId: string): void {
    if (this.treeRenderer && this.treeRenderer.zoomToTip) {
      this.treeRenderer.zoomToTip(leafId);
      console.log('[App] Zoomed to tip:', leafId);
    } else {
      console.warn('[App] Tree renderer does not support zoom to tip');
    }
  }

  /**
   * Handle tree node click
   * @param {string} leafId - The ID of the clicked node
   * @param {object} node - Optional full node object (used by Tableau extension for clade filtering)
   */
  handleTreeNodeClick(leafId: string, node: any = null): void {
    // Base implementation only handles leaf nodes
    // Tableau extension overrides this to add clade filtering support
    if (node && !node.isLeaf) {
      // Internal node clicked - base class does nothing
      // Subclass (Tableau extension) will handle this
      console.log('[TheiaPhyloViewer] Internal node clicked:', leafId);
      return;
    }

    const rowIndices = TreeMetadataSync.treeSelectionsToRows([leafId]);

    this.selectedRows.clear();
    rowIndices.forEach((idx: number) => this.selectedRows.add(idx));

    // Update table UI
    this.updateTableSelection();
    this.updateSelectionInfo();

    // Scroll to the first selected row
    if (rowIndices.length > 0) {
      this.scrollToRow(rowIndices[0]!);
    }
  }

  /**
   * Handle lasso selection - select multiple leaves
   * @param {Array<string>} leafIds - Array of leaf IDs selected by lasso
   * @param {string} source - Selection source ('lasso' or 'legend')
   */
  handleLassoSelection(leafIds: string[], source: string = 'lasso'): void {
    console.log(`[TheiaPhyloViewer] ${source} selection:`, leafIds.length, 'leaves');

    // Convert leaf IDs to row indices
    const rowIndices = TreeMetadataSync.treeSelectionsToRows(leafIds);

    // Clear current selection and add all lasso-selected rows
    this.selectedRows.clear();
    rowIndices.forEach((idx: number) => this.selectedRows.add(idx));

    // Update table UI
    this.updateTableSelection();
    this.updateSelectionInfo();

    // Highlight tree nodes
    this.highlightTreeNodes(leafIds);

    // Scroll to the first selected row
    if (rowIndices.length > 0) {
      this.scrollToRow(rowIndices[0]!);
    }

    // Show notification with appropriate message based on source
    const sourceLabel = source === 'legend' ? 'legend' : 'lasso';
    this.showNotification(`Selected ${leafIds.length} leaves via ${sourceLabel}`, 'success');
  }

  /**
   * Update table row selection styling
   */
  updateTableSelection(): void {
    const tbody = document.getElementById('table-body') as HTMLElement;
    const rows = tbody.querySelectorAll('tr');

    rows.forEach(tr => {
      const rowIndex = parseInt((tr as HTMLElement).dataset['rowIndex']!);
      if (this.selectedRows.has(rowIndex)) {
        tr.classList.add('selected');
      } else {
        tr.classList.remove('selected');
      }
    });
  }

  /**
   * Highlight tree nodes
   */
  highlightTreeNodes(leafIds: string[]): void {
    if (!this.treeRenderer) return;

    try {
      this.treeRenderer.setSelectedIds(leafIds);
    } catch (error) {
      console.error('Failed to highlight tree nodes:', error);
    }
  }

  /**
   * Update selection info display
   */
  updateSelectionInfo(): void {
    const info = document.getElementById('selection-info') as HTMLElement;
    const clearSelectionBtn = document.getElementById('clear-selection-btn') as HTMLElement | null;
    const clearSelectionBtnTree = document.getElementById('clear-selection-btn-tree') as HTMLElement | null;
    const count = this.selectedRows.size;

    if (count > 0) {
      info.textContent = `${count} row${count !== 1 ? 's' : ''} selected`;
      // Show clear selection buttons when items are selected
      if (clearSelectionBtn) {
        clearSelectionBtn.style.display = '';
      }
      if (clearSelectionBtnTree) {
        clearSelectionBtnTree.style.display = 'flex';
      }
    } else {
      info.textContent = '';
      // Hide clear selection buttons when nothing is selected
      if (clearSelectionBtn) {
        clearSelectionBtn.style.display = 'none';
      }
      if (clearSelectionBtnTree) {
        clearSelectionBtnTree.style.display = 'none';
      }
    }
  }

  /**
   * Update mapping info display
   */
  updateMappingInfo(): void {
    const info = document.getElementById('mapping-info') as HTMLElement | null;

    // Skip if no metadata (tree-only mode)
    if (!this.metadata || this.metadata.length === 0) {
      if (info) {
        info.textContent = 'Tree-only mode (no metadata)';
        info.style.color = '#666';
      }
      return;
    }

    const mappedRows = TreeMetadataSync.getMappedRows();
    const totalRows = this.metadata.length;
    const mappedCount = mappedRows.length;

    // Check if we're in subtree filtered view
    if (this.filteredTipIds && this.filteredTipIds.length > 0) {
      const filteredCount = this.filteredTipIds.length;
      if (info) {
        info.textContent = `Subtree View: ${filteredCount}/${totalRows} rows`;
        info.style.color = '#1976d2';
      }
      return;
    }

    if (!info) return;

    if (mappedCount === totalRows) {
      info.textContent = `✓ All ${totalRows} rows mapped`;
      info.style.color = '#2e7d32';
    } else if (mappedCount > 0) {
      info.textContent = `${mappedCount}/${totalRows} rows mapped`;
      info.style.color = '#f57c00';
    } else {
      info.textContent = `⚠ No rows mapped`;
      info.style.color = '#d32f2f';
    }
  }

  /**
   * Scroll to a specific row in the table
   */
  scrollToRow(rowIndex: number): void {
    const tbody = document.getElementById('table-body') as HTMLElement;
    const rows = tbody.querySelectorAll('tr');

    rows.forEach(tr => {
      const trRowIndex = parseInt((tr as HTMLElement).dataset['rowIndex']!);
      if (trRowIndex === rowIndex) {
        tr.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    });
  }

  /**
   * Get sorted metadata
   */
  getSortedMetadata(): Array<{ row: any; originalIndex: number }> {
    if (!this.metadata) {
      return [];
    }

    const data = this.metadata.map((row, index) => ({ row, originalIndex: index }));

    if (!this.sortColumn) {
      return data;
    }

    return data.sort((a, b) => {
      const valA = a.row[this.sortColumn!];
      const valB = b.row[this.sortColumn!];

      let comparison = 0;
      if (valA < valB) comparison = -1;
      if (valA > valB) comparison = 1;

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  /**
   * Get sort indicator for column
   */
  getSortIndicator(column: string): string {
    if (this.sortColumn !== column) return '';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Handle column sort
   */
  handleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.renderTable();
  }

  /**
   * Handle checkbox change
   */
  handleCheckboxChange(rowIndex: number, checked: boolean): void {
    if (checked) {
      this.selectedRows.add(rowIndex);
    } else {
      this.selectedRows.delete(rowIndex);
    }

    this.updateTableSelection();
    this.updateSelectAllCheckbox();

    const leafIds = TreeMetadataSync.rowSelectionsToTreeLeaves(
      Array.from(this.selectedRows)
    );
    this.highlightTreeNodes(leafIds);
    this.updateSelectionInfo();
  }

  /**
   * Handle select all checkbox
   */
  handleSelectAll(checked: boolean): void {
    if (checked) {
      this.metadata.forEach((_, index) => this.selectedRows.add(index));
    } else {
      this.selectedRows.clear();
    }

    this.updateTableSelection();

    const leafIds = TreeMetadataSync.rowSelectionsToTreeLeaves(
      Array.from(this.selectedRows)
    );
    this.highlightTreeNodes(leafIds);
    this.updateSelectionInfo();
  }

  /**
   * Update select all checkbox state
   */
  updateSelectAllCheckbox(): void {
    const selectAllCheckbox = document.getElementById('select-all-checkbox') as HTMLInputElement | null;
    if (selectAllCheckbox && this.metadata) {
      selectAllCheckbox.checked = this.selectedRows.size === this.metadata.length;
      selectAllCheckbox.indeterminate = this.selectedRows.size > 0 && this.selectedRows.size < this.metadata.length;
    }
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    this.selectedRows.clear();

    // Update all checkboxes in the table
    const checkboxes = document.querySelectorAll('#metadata-table tbody input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      (checkbox as HTMLInputElement).checked = false;
    });

    this.updateTableSelection();
    this.updateSelectAllCheckbox();
    this.highlightTreeNodes([]);
    this.updateSelectionInfo();
  }

  /**
   * Update label field and rebuild mapping
   */
  updateLabelField(labelField: string): void {
    console.log('[TheiaPhyloViewer] Updating label field to:', labelField);

    // Rebuild tree-metadata mapping with new label field
    const treeData = this.parseTree(this.tree);
    TreeMetadataSync.buildLabelMapping(treeData, this.metadata, labelField);

    // Clear current selection as row mappings have changed
    this.selectedRows.clear();

    // Update tree colors if a color field is selected
    if (TreeMetadataSync.state.colorByField) {
      this.updateTreeColors();
    }

    // Update metadata blocks if color by field is set
    if (TreeMetadataSync.state.colorByField) {
      this.updateMetadataBlocks(true);
    }

    // Update table to reflect new mapping
    this.renderTable();

    console.log('[TheiaPhyloViewer] Label field updated successfully');
  }

  /**
   * Set label field from column header click
   */
  setLabelFieldFromColumn(columnName: string): void {
    console.log('[TheiaPhyloViewer] Setting label field from column:', columnName);

    // Update the label field
    this.updateLabelField(columnName);

    // Show a brief visual feedback
    this.showToast(`Label field set to: ${columnName}`, 'info');
  }

  /**
   * Show a toast notification
   */
  showToast(message: string, type: string = 'info'): void {
    // Remove any existing toast
    const existingToast = document.querySelector('.phylo-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create new toast
    const toast = document.createElement('div');
    toast.className = `phylo-toast ${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    // Show toast
    setTimeout(() => toast.classList.add('show'), 10);

    // Hide and remove after 2 seconds
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  /**
   * Auto-detect best label field by comparing tree tips with metadata columns
   */
  autoDetectLabelField(treeData: { leaves: Array<{ id: string }> }, metadata: any[]): string {
    if (!metadata || metadata.length === 0 || !metadata[0]) {
      return 'id';
    }

    const treeLabels = TreeMetadataSync.extractTreeLabels(treeData);
    const columns = Object.keys(metadata[0]);

    let bestField = columns[0]!;
    let maxMatches = 0;

    columns.forEach((field: string) => {
      let matches = 0;
      metadata.forEach((row: any) => {
        if (treeLabels.has(row[field])) {
          matches++;
        }
      });

      if (matches > maxMatches) {
        maxMatches = matches;
        bestField = field;
      }
    });

    console.log('[TheiaPhyloViewer] Auto-detected label field:', bestField, 'with', maxMatches, 'matches out of', treeLabels.size, 'tree tips');

    return bestField;
  }

  /**
   * Save current panel sizes
   */
  savePanelSizes(): void {
    const treePanel = document.querySelector('.tree-panel') as HTMLElement | null;
    const tablePanel = document.querySelector('.table-panel') as HTMLElement | null;

    if (treePanel && tablePanel) {
      this.saveSettings({
        panelSizes: {
          treeFlex: treePanel.style.flex,
          tableFlex: tablePanel.style.flex
        }
      });
    }
  }

  /**
   * Apply saved panel sizes
   */
  applyPanelSizes(panelSizes: { treeFlex?: string; tableFlex?: string }): void {
    const treePanel = document.querySelector('.tree-panel') as HTMLElement | null;
    const tablePanel = document.querySelector('.table-panel') as HTMLElement | null;

    if (treePanel && tablePanel && panelSizes) {
      if (panelSizes.treeFlex) {
        treePanel.style.flex = panelSizes.treeFlex;
      }
      if (panelSizes.tableFlex) {
        tablePanel.style.flex = panelSizes.tableFlex;
      }

      // Trigger tree resize after applying panel sizes
      setTimeout(() => {
        if (this.treeRenderer && this.treeRenderer.resize) {
          this.treeRenderer.resize();
        }
      }, 100);
    }
  }

  /**
   * Initialize resize handle for horizontal panel resizing
   */
  initResizeHandle(): void {
    // Skip if already initialized
    if (this._resizeHandleInitialized) return;

    const resizeHandle = document.getElementById('resize-handle') as HTMLElement;
    const treePanel = document.querySelector('.tree-panel') as HTMLElement;
    const tablePanel = document.querySelector('.table-panel') as HTMLElement;
    const container = document.querySelector('.container') as HTMLElement;

    let isResizing = false;
    let startX = 0;
    let startTreeWidth = 0;
    let containerWidth = 0;
    let animationFrameId: number | null = null;

    const startResize = (e: MouseEvent) => {
      isResizing = true;
      startX = e.clientX;
      startTreeWidth = treePanel.offsetWidth;
      containerWidth = container.offsetWidth;

      // Disable transitions during drag for smooth performance
      treePanel.style.transition = 'none';
      tablePanel.style.transition = 'none';

      resizeHandle.classList.add('dragging');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const resize = (e: MouseEvent) => {
      if (!isResizing) return;

      // Use requestAnimationFrame for smooth 60fps updates
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const handleWidth = 10; // Fixed width, no need to query DOM
        const deltaX = e.clientX - startX;
        const newTreeWidth = startTreeWidth + deltaX;

        // Calculate percentages
        const minWidth = 200;
        const maxTreeWidth = containerWidth - handleWidth - minWidth;

        if (newTreeWidth >= minWidth && newTreeWidth <= maxTreeWidth) {
          const treePercent = (newTreeWidth / containerWidth) * 100;
          const tablePercent = ((containerWidth - newTreeWidth - handleWidth) / containerWidth) * 100;

          treePanel.style.flex = `0 0 ${treePercent}%`;
          tablePanel.style.flex = `0 0 ${tablePercent}%`;
        }
      });
    };

    const stopResize = () => {
      if (!isResizing) return;
      isResizing = false;

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }

      // Re-enable transitions
      treePanel.style.transition = '';
      tablePanel.style.transition = '';

      resizeHandle.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Trigger tree resize after panel resize
      requestAnimationFrame(() => {
        if (this.treeRenderer && this.treeRenderer.resize) {
          this.treeRenderer.resize();
        }
      });
    };

    resizeHandle.addEventListener('mousedown', startResize);
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);

    this._resizeHandleInitialized = true;
  }

  /**
   * Toggle table panel collapse
   */
  toggleTableCollapse(): void {
    this.isTableCollapsed = !this.isTableCollapsed;
    const tablePanel = document.getElementById('table-panel') as HTMLElement;
    const treePanel = document.querySelector('.tree-panel') as HTMLElement;
    const collapseIcon = document.getElementById('panel-collapse-icon') as HTMLElement;
    const resizeHandle = document.getElementById('resize-handle') as HTMLElement;

    if (this.isTableCollapsed) {
      tablePanel.classList.add('collapsed');
      collapseIcon.textContent = '◀';
      resizeHandle.style.display = 'none';
      // Expand tree panel to take full space
      treePanel.style.flex = '1';
    } else {
      tablePanel.classList.remove('collapsed');
      collapseIcon.textContent = '▼';
      resizeHandle.style.display = 'block';
      // Reset to default split
      treePanel.style.flex = '0 0 50%';
      tablePanel.style.flex = '0 0 calc(50% - 10px)';
    }

    // Wait for CSS transition and force layout recalculation, then resize tree
    setTimeout(() => {
      if (this.treeRenderer && this.treeRenderer.resize) {
        console.log('[TheiaPhyloViewer] Resizing tree renderer after panel collapse');
        this.treeRenderer.resize();
      }
    }, 350); // Wait for CSS transition
  }

  /**
   * Handle row hover - highlight corresponding tree tip
   */
  handleRowHover(rowIndex: number): void {
    // Get the leaf IDs for this row
    const leafIds = TreeMetadataSync.rowSelectionsToTreeLeaves([rowIndex]);

    if (leafIds.length === 0) return;

    // Add hover highlight to the table row
    const tbody = document.getElementById('table-body') as HTMLElement;
    const rows = tbody.querySelectorAll('tr');
    rows.forEach(tr => {
      const trRowIndex = parseInt((tr as HTMLElement).dataset['rowIndex']!);
      if (trRowIndex === rowIndex) {
        tr.classList.add('hover-highlight');
      }
    });

    // Temporarily highlight the tree nodes
    if (this.treeRenderer) {
      this.treeRenderer.setHoveredIds(leafIds);
    }
  }

  /**
   * Handle row hover end - remove temporary highlight
   */
  handleRowHoverEnd(): void {
    // Remove hover highlight from all table rows
    const tbody = document.getElementById('table-body') as HTMLElement;
    const rows = tbody.querySelectorAll('tr');
    rows.forEach(tr => {
      tr.classList.remove('hover-highlight');
    });

    // Clear temporary tree node highlight
    if (this.treeRenderer) {
      this.treeRenderer.setHoveredIds([]);
    }
  }

  /**
   * Initialize search controls
   */
  initSearchControls(): void {
    const searchInput = document.getElementById('searchInput') as HTMLInputElement | null;
    const clearSearchBtn = document.getElementById('clearSearchBtn') as HTMLButtonElement | null;
    const searchMenuBtn = document.getElementById('searchMenuBtn') as HTMLButtonElement | null;

    if (!searchInput) return;

    // Update tip labels from current tree
    const treeData = this.parseTree(this.tree);
    this.allTipLabels = treeData.leaves.map(leaf => leaf.id);
    console.log('[TheiaPhyloViewer] Updated allTipLabels:', this.allTipLabels.length, 'tips');

    // Clear search when tree changes
    if (this._searchControlsInitialized) {
      this.clearSearch();
      return;
    }

    // Search input handler
    searchInput.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.performSearch();
    });

    // Clear search handler
    if (clearSearchBtn) {
      clearSearchBtn.addEventListener('click', () => {
        this.clearSearch();
      });
    }

    // Search menu/help handler
    if (searchMenuBtn) {
      searchMenuBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleSearchHelp();
      });
    }

    // ESC key to clear search
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.clearSearch();
      }
    });

    this._searchControlsInitialized = true;

    // Update initial state
    this.updateSearchUI();
  }

  /**
   * Toggle search help/guidelines
   */
  toggleSearchHelp(): void {
    // Check if help tooltip already exists
    let helpTooltip = document.getElementById('search-help-tooltip') as HTMLElement | null;

    if (helpTooltip) {
      // Remove existing tooltip
      helpTooltip.remove();
      return;
    }

    const searchMenuBtn = document.getElementById('searchMenuBtn') as HTMLButtonElement | null;

    // Create help tooltip
    helpTooltip = document.createElement('div');
    helpTooltip.id = 'search-help-tooltip';
    helpTooltip.className = 'search-help-tooltip';
    helpTooltip.innerHTML = `
      <div class="search-help-header">
        <strong>Search Guidelines</strong>
        <button class="search-help-close">×</button>
      </div>
      <div class="search-help-content">
        <div class="search-help-section">
          <div class="search-help-title">Basic Search</div>
          <div class="search-help-example">
            <code>Sample_001</code> - Find tips/rows containing this text
          </div>
        </div>

        <div class="search-help-section">
          <div class="search-help-title">OR Operator</div>
          <div class="search-help-example">
            <code>UK OR USA</code> - Find rows with UK <strong>or</strong> USA
          </div>
          <div class="search-help-note">Matches if ANY term is found</div>
        </div>

        <div class="search-help-section">
          <div class="search-help-title">AND Operator</div>
          <div class="search-help-example">
            <code>UK AND Sensitive</code> - Find rows with <strong>both</strong> UK and Sensitive
          </div>
          <div class="search-help-note">Matches if ALL terms are found</div>
        </div>

        <div class="search-help-section">
          <div class="search-help-title">Search Scope</div>
          <div class="search-help-note">
            • Searches all metadata columns<br>
            • Searches tree tip labels<br>
            • Case-insensitive<br>
            • Partial matching supported
          </div>
        </div>
      </div>
    `;

    // Add to document
    document.body.appendChild(helpTooltip);

    // Position near search container
    const searchContainer = document.querySelector('.search-container') as HTMLElement;
    const rect = searchContainer.getBoundingClientRect();
    helpTooltip.style.top = `${rect.bottom + 5}px`;
    helpTooltip.style.left = `${rect.left}px`;

    // Close button handler
    const closeBtn = helpTooltip.querySelector('.search-help-close') as HTMLButtonElement;
    closeBtn.addEventListener('click', () => {
      helpTooltip!.remove();
    });

    // Close when clicking outside
    setTimeout(() => {
      document.addEventListener('click', function closeHelp(e) {
        if (!helpTooltip!.contains(e.target as Node) && !searchMenuBtn?.contains(e.target as Node)) {
          helpTooltip!.remove();
          document.removeEventListener('click', closeHelp);
        }
      });
    }, 100);
  }

  /**
   * Parse search query to extract terms and operators
   * Supports: "term1 AND term2", "term1 OR term2", "term1 term2" (default OR)
   */
  parseSearchQuery(query: string): { operator: string; terms: string[] } {
    query = query.trim();

    // Check if query contains AND or OR operators
    const hasAND = /\bAND\b/i.test(query);
    const hasOR = /\bOR\b/i.test(query);

    if (hasAND) {
      // Split by AND (case insensitive)
      const terms = query.split(/\bAND\b/i).map((t: string) => t.trim().toLowerCase()).filter((t: string) => t);
      return { operator: 'AND', terms };
    } else if (hasOR) {
      // Split by OR (case insensitive)
      const terms = query.split(/\bOR\b/i).map((t: string) => t.trim().toLowerCase()).filter((t: string) => t);
      return { operator: 'OR', terms };
    } else {
      // Default: treat as single term
      return { operator: 'OR', terms: [query.toLowerCase()] };
    }
  }

  /**
   * Check if a row matches the search criteria
   */
  rowMatchesSearch(row: Record<string, any>, searchTerms: string[], operator: string): boolean {
    if (operator === 'AND') {
      // ALL terms must match
      return searchTerms.every(term => {
        return Object.values(row).some(value => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(term);
        });
      });
    } else {
      // ANY term must match (OR)
      return searchTerms.some(term => {
        return Object.values(row).some(value => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(term);
        });
      });
    }
  }

  /**
   * Perform search on tree tips and metadata
   */
  performSearch(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.clearSearch();
      return;
    }

    // Parse query to get terms and operator
    const { operator, terms } = this.parseSearchQuery(this.searchQuery);
    const matchedTipSet = new Set<string>();

    console.log('[TheiaPhyloViewer] Search query parsed:', { operator, terms });

    // Search in tip labels
    this.allTipLabels.forEach(label => {
      const labelLower = label.toLowerCase();

      if (operator === 'AND') {
        // Label must contain ALL terms
        if (terms.every(term => labelLower.includes(term))) {
          matchedTipSet.add(label);
        }
      } else {
        // Label must contain ANY term
        if (terms.some(term => labelLower.includes(term))) {
          matchedTipSet.add(label);
        }
      }
    });

    // Search in metadata table - check all columns for all rows
    this.metadata.forEach((row, rowIndex) => {
      if (this.rowMatchesSearch(row, terms, operator)) {
        // Get the tree labels for this row
        const labels = TreeMetadataSync.state.labelsByRow.get(rowIndex);
        if (labels) {
          labels.forEach((label: string) => matchedTipSet.add(label));
        }
      }
    });

    // Convert set to array
    this.matchedTips = Array.from(matchedTipSet);

    console.log('[TheiaPhyloViewer] Search results:', {
      query: this.searchQuery,
      operator,
      terms,
      matched: this.matchedTips.length,
      total: this.allTipLabels.length
    });

    // Update UI
    this.updateSearchUI();

    // Apply filtering to tree renderer
    if (this.treeRenderer && this.treeRenderer.setFilteredTips) {
      this.treeRenderer.setFilteredTips(this.matchedTips);
    }
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.matchedTips = [];
    this.currentMatchIndex = -1;

    const searchInput = document.getElementById('searchInput') as HTMLInputElement | null;
    if (searchInput) {
      searchInput.value = '';
    }

    // Update UI
    this.updateSearchUI();

    // Clear filtering from tree renderer
    if (this.treeRenderer && this.treeRenderer.setFilteredTips) {
      this.treeRenderer.setFilteredTips([]);
    }
  }

  /**
   * Update search UI elements
   */
  updateSearchUI(): void {
    const resultsSummaryEl = document.getElementById('searchResultsSummary') as HTMLElement | null;
    const clearBtn = document.getElementById('clearSearchBtn') as HTMLElement | null;

    if (resultsSummaryEl) {
      const total = this.allTipLabels.length;
      const matched = this.searchQuery ? this.matchedTips.length : total;
      resultsSummaryEl.textContent = `${matched} of ${total}`;
    }

    if (clearBtn) {
      clearBtn.style.display = this.searchQuery ? 'block' : 'none';
    }
  }

}

export async function initStandalone(): Promise<void> {
  // Skip initialization if running as Tableau extension
  if (typeof tableau !== 'undefined' && tableau.extensions) {
    console.log('[app.js] Running in Tableau mode, skipping standalone initialization');
    return;
  }

  const viewer = new TheiaPhyloViewer();

  // Make viewer accessible globally for debugging
  (window as any).theiaViewer = viewer;

  // Initialize upload handler
  const uploadHandler = new UploadHandler(viewer);
  (window as any).uploadHandler = uploadHandler;

  // Initialize help modal
  const helpBtn = document.getElementById('help-btn') as HTMLButtonElement | null;
  const helpModal = document.getElementById('help-modal') as HTMLElement | null;
  const helpModalCloseBtn = document.getElementById('help-modal-close-btn') as HTMLButtonElement | null;

  if (helpBtn && helpModal) {
    // Open help modal
    helpBtn.addEventListener('click', () => {
      helpModal.classList.remove('hidden');
    });

    // Close help modal
    helpModalCloseBtn!.addEventListener('click', () => {
      helpModal.classList.add('hidden');
    });

    // Close modal when clicking outside
    helpModal.addEventListener('click', (e) => {
      if (e.target === helpModal) {
        helpModal.classList.add('hidden');
      }
    });

    // Close modal with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !helpModal.classList.contains('hidden')) {
        helpModal.classList.add('hidden');
      }
    });
  }

  // Initialize header collapse/expand functionality
  const headerToggleBtn = document.getElementById('header-toggle-btn') as HTMLButtonElement | null;
  const header = document.querySelector('.header') as HTMLElement | null;

  if (headerToggleBtn && header) {
    headerToggleBtn.addEventListener('click', () => {
      header.classList.toggle('collapsed');

      // Save state to localStorage
      const isCollapsed = header.classList.contains('collapsed');
      localStorage.setItem('headerCollapsed', String(isCollapsed));
    });

    // Restore collapsed state from localStorage
    const savedState = localStorage.getItem('headerCollapsed');
    if (savedState === 'true') {
      header.classList.add('collapsed');
    }
  }

  // Add debug helpers to window
  (window as any).debugSettings = {
    view: () => {
      console.log('=== Current Settings ===');
      const settings = viewer.loadSettings();
      console.log(JSON.stringify(settings, null, 2));
      return settings;
    },
    clear: () => {
      viewer.clearSettings();
      console.log('Settings cleared! Reload the page to see default settings.');
    },
    save: (key: string, value: any) => {
      const obj: Record<string, any> = {};
      obj[key] = value;
      viewer.saveSettings(obj);
      console.log('Saved:', obj);
    },
    test: () => {
      try {
        localStorage.setItem('test', 'value');
        const result = localStorage.getItem('test');
        localStorage.removeItem('test');
        console.log('localStorage is working!');
        return true;
      } catch (e) {
        console.error('localStorage error:', e);
        return false;
      }
    }
  };

  console.log('%c=== Kronos Debug Mode ===', 'color: #1976d2; font-weight: bold; font-size: 14px;');
  console.log('Use these commands in the console:');
  console.log('  debugSettings.view()  - View current saved settings');
  console.log('  debugSettings.clear() - Clear all saved settings');
  console.log('  debugSettings.test()  - Test if localStorage is working');
  console.log('  window.theiaViewer    - Access the viewer instance');

  // Example configuration - replace with your data
  const config = {
    treeUrl: 'data/tree.nwk',
    metadataUrl: 'data/metadata.csv',
    labelField: 'id'  // Column in metadata that matches tree leaf labels
  };

  await viewer.init(config);
}
